import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { classifyUploadError } from '@/shared/utils/classify-upload-error/classify-upload-error.util';

import { type UseMediaUpload, type UseMediaUploadInput } from './useMediaUpload.types';

/**
 * The upload state machine every media control shares: one file, a progress
 * number, a cancel, and one line of copy when it fails.
 *
 * **Extracted rather than copied a second time** (P10.2c's `useLiveAnnouncer`
 * precedent). It was `useMediaPickerLogic`'s private half until P23b4 needed the
 * same behaviour for a payment receipt, which is a control with no library, no
 * `MediaSource` and no stored uuid — so reusing the picker was not an option and
 * duplicating its machine would have meant two places to fix the next abort bug.
 * The picker's own spec is untouched, which is the proof this changed nothing.
 *
 * What it deliberately does **not** own: the route. The caller supplies `run`,
 * so the brand library's `admin/media` pair and an order's own evidence pair are
 * the same code path here.
 *
 * Three behaviours are load-bearing and each is asserted:
 *
 * - **One file.** Every consumer stores a single id, so uploading the rest would
 *   spend the deployment's storage on assets nothing will ever point at.
 * - **An unmount aborts.** Otherwise the bytes land, the row finalizes, and
 *   nothing points at it — an orphan created by navigating away.
 * - **A cancel is not a failure.** `classifyUploadError` answers `null` for it,
 *   and a line of red text under an upload the operator stopped themselves reads
 *   as a problem they now have to think about.
 */
export function useMediaUpload<TResult>({
  run,
  onUploaded,
  announcer,
  isLocked = false,
}: UseMediaUploadInput<TResult>): UseMediaUpload {
  const { t } = useTranslation('uiKit');
  const { say, announceReadOnlyRefusal } = announcer;

  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const start = useCallback(
    async (files: readonly File[] | FileList) => {
      const list = Array.from(files);

      if (list.length === 0) {
        return;
      }

      if (isLocked) {
        announceReadOnlyRefusal();
        return;
      }

      const [file] = list;

      if (file === undefined) {
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setErrorKey(null);
      setProgress(null);
      setIsUploading(true);
      // Announced when it STARTS, not only when it ends. The walk found this
      // gap: a screen-reader user pressed upload and heard nothing at all until
      // it finished — and on a slow connection that is a long silence with a
      // moving bar they cannot see. The percentage is deliberately NOT repeated
      // here; it rides on the progress bar's own `aria-valuenow`, and a live
      // region firing every hundred milliseconds is unusable.
      say(t('media.announce.uploading'));

      try {
        const result = await run(file, {
          signal: controller.signal,
          onProgress: (loaded, total) => {
            setProgress(total === 0 ? null : Math.round((loaded / total) * 100));
          },
        });

        await onUploaded(result);
        say(t('media.announce.uploaded'));
      } catch (error) {
        // `null` for a cancel — the operator did it and needs no explaining to.
        const key = classifyUploadError(error);
        setErrorKey(key);
        say(key === null ? t('media.announce.canceled') : t(key));
      } finally {
        abortRef.current = null;
        setIsUploading(false);
        setProgress(null);
      }
    },
    [announceReadOnlyRefusal, isLocked, onUploaded, run, say, t],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clearError = useCallback(() => {
    setErrorKey(null);
  }, []);

  return { isUploading, progress, errorKey, start, cancel, clearError };
}
